import { Cross1Icon } from '@radix-ui/react-icons';
import { Dialog, Flex, IconButton, VisuallyHidden } from '@radix-ui/themes';
import { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryItem } from '../../types';
import { DeliverySuccessView } from '../DeliverySuccessView';
import { useDeliveryContext } from './DeliveryContext';

export interface ContentProps extends ComponentProps<typeof Dialog.Content> {
    title: string;
    a11yDescription: string;
    children: ReactNode;
    successTitle: string;
    successDescription: string;
}

export const Content = <T extends DeliveryItem, P>({
    title,
    a11yDescription,
    children,
    successTitle,
    successDescription,
    ...rest
}: ContentProps) => {
    const { isOpen, setIsOpen, generatedResult } = useDeliveryContext<T, P>();
    const { t } = useTranslation('common');

    return (
        <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Content
                maxWidth='550px'
                style={{ padding: '40px' }}
                aria-describedby={undefined}
                {...rest}
            >
                <Dialog.Close>
                    <IconButton
                        variant='ghost'
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                        }}
                    >
                        <Cross1Icon />
                    </IconButton>
                </Dialog.Close>

                {generatedResult ? (
                    <DeliverySuccessView
                        title={successTitle}
                        description={successDescription}
                        result={generatedResult}
                        closeLabel={t('buttons.close')}
                        onClose={() => setIsOpen(false)}
                    />
                ) : (
                    <Flex direction='column' gap='4'>
                        <Dialog.Title align='center'>{title}</Dialog.Title>
                        <VisuallyHidden>
                            <Dialog.Description>
                                {a11yDescription}
                            </Dialog.Description>
                        </VisuallyHidden>

                        {children}
                    </Flex>
                )}
            </Dialog.Content>
        </Dialog.Root>
    );
};
